import React, { useDeferredValue, useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { FiSliders, FiCalendar } from 'react-icons/fi';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchTransactionsForMonth, fetchTransactionRange } from '../store/slices/transactionsSlice';
import { fetchTags, fetchTagsByType, selectTagMap } from '../store/slices/tagsSlice';
import { fetchTransactionYearRange } from '../services/apiService';
import { Transaction, Tag } from '../types';
import CurrencyDisplay from '../components/AmountDisplay';
import DraggableBottomSheet from '../components/DraggableBottomSheet';
import ScreenContainer from '../components/ScreenContainer';
import SpendingSummaryScreenSkeleton from '../components/skeletons/SpendingSummaryScreenSkeleton';
import { useNavigation } from '../hooks/useNavigation';
import { getTopLevelTags, normalizeTagKey, normalizeTagName, UNTAGGED_KEY } from '../utils/tagHelpers';
import { isInvestmentDebitTransaction } from '../utils/cashFlowUtils';
import { getCurrentKolkataYearMonth, parseTransactionDateParts } from '../utils/dateTimeUtils';

interface TagSpending {
    key: string;
    name: string;
    amount: number;
}

interface SpendingSummaryRouteState {
    selectedYear?: number;
    selectedMonth?: string;
}

const SpendingSummaryScreen: React.FC = () => {
    const dispatch = useAppDispatch();
    const { navigateTo } = useNavigation();
    const location = useLocation();
    const routeState = location.state as SpendingSummaryRouteState | null;

    // --- State for Filters ---
    const [isMonthFilterOpen, setIsMonthFilterOpen] = useState(false);
    const currentKolkataYearMonth = getCurrentKolkataYearMonth();
    const defaultSelectedMonth = new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, currentKolkataYearMonth.month - 1, 1)));
    const [selectedMonth, setSelectedMonth] = useState<string>(routeState?.selectedMonth ?? defaultSelectedMonth);
    const [selectedYear, setSelectedYear] = useState<number>(
        routeState?.selectedYear && Number.isFinite(routeState.selectedYear)
            ? routeState.selectedYear
            : currentKolkataYearMonth.year
    );
    const [availableYearsFromApi, setAvailableYearsFromApi] = useState<number[]>([]);

    const availableMonths = useMemo(() => {
        // TODO: Replace with dynamic logic based on available data/year
        // Represent months with names and their numeric value (1-indexed)
        return [
            { name: 'All Months', value: 0 }, // Use 0 or null for All Months
            { name: 'January', value: 1 },
            { name: 'February', value: 2 },
            { name: 'March', value: 3 },
            { name: 'April', value: 4 },
            { name: 'May', value: 5 },
            { name: 'June', value: 6 },
            { name: 'July', value: 7 },
            { name: 'August', value: 8 },
            { name: 'September', value: 9 },
            { name: 'October', value: 10 },
            { name: 'November', value: 11 },
            { name: 'December', value: 12 },
        ];
    }, []);

    const {
        transactions, // Use main transactions list
        status: transactionsStatus, // Use main status
    } = useAppSelector((state) => state.transactions);
    const {
        debitTags,
        status: tagsStatus,
        debitStatus,
    } = useAppSelector((state) => state.tags);
    const tagMap = useAppSelector(selectTagMap);
    const deferredTransactions = useDeferredValue(transactions);
    const deferredDebitTags = useDeferredValue(debitTags);
    const deferredTagMap = useDeferredValue(tagMap);

    // --- Available Years ---
    const availableYears = useMemo(() => {
        const { year: currentYear } = getCurrentKolkataYearMonth();
        const uniqueYears = Array.from(
            new Set([selectedYear, currentYear, ...availableYearsFromApi])
        );

        uniqueYears.sort((a, b) => b - a);
        return uniqueYears;
    }, [availableYearsFromApi, selectedYear]);
    // --- End Available Years ---

    const isLoading =
        transactionsStatus === 'loading' ||
        tagsStatus === 'loading' ||
        debitStatus === 'idle' ||
        debitStatus === 'loading';
    const selectedMonthValue = useMemo(() => {
        const selectedMonthObject = availableMonths.find((month) => month.name === selectedMonth);
        return selectedMonthObject?.value;
    }, [availableMonths, selectedMonth]);

    useEffect(() => {
        const currentHistoryState = window.history.state;
        const currentRouteState = (currentHistoryState?.usr ?? {}) as SpendingSummaryRouteState;
        const nextRouteState: SpendingSummaryRouteState = {
            ...currentRouteState,
            selectedYear,
            selectedMonth,
        };

        window.history.replaceState(
            {
                ...currentHistoryState,
                usr: nextRouteState,
            },
            '',
        );
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        let isMounted = true;

        const loadAvailableYears = async () => {
            try {
                const response = await fetchTransactionYearRange();
                const years = (response.years || [])
                    .filter((year) => Number.isFinite(year))
                    .sort((a, b) => b - a);

                if (isMounted) {
                    setAvailableYearsFromApi(years);
                }
            } catch (fetchError) {
                console.error('Failed to fetch transaction year range:', fetchError);
                if (isMounted) {
                    setAvailableYearsFromApi([]);
                }
            }
        };

        loadAvailableYears();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        // Fetch data for the initially selected month/year if not loaded
        if (transactionsStatus === 'idle' && selectedMonthValue && selectedMonthValue !== 0) {
            dispatch(fetchTransactionsForMonth({ year: selectedYear, month: selectedMonthValue }));
        }
        if (tagsStatus === 'idle') {
            dispatch(fetchTags());
        }
        if (debitStatus === 'idle') {
            dispatch(fetchTagsByType('DEBIT'));
        }
    }, [dispatch, selectedYear, selectedMonthValue, transactionsStatus, tagsStatus, debitStatus]);

    // --- Spending Calculation Logic (Adapted from SpendingSummary) ---
    const spendingByTag = useMemo((): TagSpending[] => {
        if (transactionsStatus !== 'succeeded' || tagsStatus !== 'succeeded' || debitStatus !== 'succeeded') {
            return [];
        }

        let filteredTransactions = deferredTransactions;
        if (selectedMonthValue && selectedMonthValue !== 0) {
            // Filter by specific month and year
            filteredTransactions = deferredTransactions.filter(tx => {
                const txDate = parseTransactionDateParts(tx.transactionDate);
                // Compare year and 1-indexed month
                return txDate.year === selectedYear && txDate.month === selectedMonthValue;
            });
        } else if (selectedMonth === 'All Months') {
            // Filter only by year when 'All Months' is selected
            filteredTransactions = deferredTransactions.filter(tx => {
                const txDate = parseTransactionDateParts(tx.transactionDate);
                return txDate.year === selectedYear;
            });
        } // If neither condition is met (e.g., initial load or error), use all transactions (though this shouldn't happen with default state)

        const directSpending = new Map<string, number>();
        filteredTransactions.forEach((tx: Transaction) => {
            if (tx.type !== 'DEBIT' || tx.excludeFromAccounting || isInvestmentDebitTransaction(tx, deferredTagMap)) {
                return;
            }
            const key = normalizeTagKey(tx.tag);
            directSpending.set(key, (directSpending.get(key) ?? 0) + tx.amount);
        });

        const calculateTotalSpending = (tag: Tag): number => {
            const own = directSpending.get(normalizeTagName(tag.name)) ?? 0;
            const childrenTotal = (tag.children ?? []).reduce((sum, child) => sum + calculateTotalSpending(child), 0);
            return own + childrenTotal;
        };

        const aggregatedSpending: TagSpending[] = getTopLevelTags(deferredDebitTags)
            .map((tag) => ({ key: normalizeTagName(tag.name), name: tag.name, amount: calculateTotalSpending(tag) }))
            .filter((item) => item.amount > 0);

        const untaggedTotal = directSpending.get(UNTAGGED_KEY) ?? 0;
        if (untaggedTotal > 0) {
            aggregatedSpending.push({ key: UNTAGGED_KEY, name: 'Untagged', amount: untaggedTotal });
        }

        aggregatedSpending.sort((a, b) => b.amount - a.amount);

        return aggregatedSpending;

    }, [deferredTransactions, deferredDebitTags, deferredTagMap, transactionsStatus, tagsStatus, debitStatus, selectedMonth, selectedMonthValue, selectedYear]);

    const totalSpending = useMemo(() => {
        return spendingByTag.reduce((sum, item) => sum + item.amount, 0);
    }, [spendingByTag]);

    const handleMonthButtonClick = () => {
        // Reset temporary filters to current selection when opening - No longer needed
        // setFilterMonth(selectedMonth);
        // setFilterYear(selectedYear);
        setIsMonthFilterOpen(true);
    };

    const handleMonthSelect = (monthName: string) => {
        // Update main state directly
        setSelectedMonth(monthName);

        // Apply filter logic immediately
        const monthObject = availableMonths.find(m => m.name === monthName);
        const monthValue = monthObject?.value;

        if (monthName === 'All Months') {
            // Dispatch fetchTransactionRange for the entire selected year
            dispatch(fetchTransactionRange({
                startYear: selectedYear,
                startMonth: 1,
                endYear: selectedYear,
                endMonth: 12
            }));
        } else if (monthValue && monthValue !== 0) { // Ensure a specific month is selected
            dispatch(fetchTransactionsForMonth({ year: selectedYear, month: monthValue }));
        } else {
            console.warn("Invalid month selection or value");
        }

        // Optionally close the sheet immediately on selection
        // setIsMonthFilterOpen(false);
    };

    // --- New handler for year selection ---
    const handleYearSelect = (year: number) => {
        // Update main state directly
        setSelectedYear(year);

        // Apply filter logic immediately
        const monthObject = availableMonths.find(m => m.name === selectedMonth);
        const monthValue = monthObject?.value;

        if (selectedMonth === 'All Months') {
            // If 'All Months' is selected, fetch the range for the new year
            dispatch(fetchTransactionRange({
                startYear: year,
                startMonth: 1,
                endYear: year,
                endMonth: 12
            }));
        } else if (monthValue && monthValue !== 0) { // Ensure a specific month is selected
            // Fetch specific month for the newly selected year
            dispatch(fetchTransactionsForMonth({ year: year, month: monthValue }));
        } else {
            // This case might not be relevant if a month is always selected, but kept for safety
            console.warn("No valid month selected for filtering when changing year");
        }

        // Optionally close the sheet immediately on selection
        // setIsMonthFilterOpen(false);
    };
    // --- End new handler ---

    const handleTagItemClick = useCallback((item: TagSpending) => {
        navigateTo(`/spending-summary/category/${encodeURIComponent(item.name)}`, {
            state: {
                tagName: item.name,
                initialYear: selectedYear,
                initialMonth: selectedMonthValue && selectedMonthValue !== 0 ? selectedMonthValue : undefined,
                initialGranularity: selectedMonthValue && selectedMonthValue !== 0 ? 'monthly' : 'yearly',
            },
        });
    }, [navigateTo, selectedMonthValue, selectedYear]);



    // --- UI Rendering --- //
    return (
        <ScreenContainer title="Spending Summary">
            <div className="flex flex-col w-full justify-center items-center">
                {isLoading || transactionsStatus === 'loadingMore' || transactionsStatus === 'idle' ? (
                    <SpendingSummaryScreenSkeleton />
                ) : (
                    <>
                        {/* Filters */}
                        <div className="flex items-center justify-center p-4 space-x-2 flex-shrink-0">
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleMonthButtonClick} // Open sheet on click
                                    className="flex items-center space-x-1 px-3 py-1.5 bg-input rounded-lg text-sm"
                                >
                                    <FiCalendar size={16} />
                                    <span>{selectedYear}</span>
                                </button>
                                <button
                                    onClick={handleMonthButtonClick} // Open sheet on click
                                    className="flex items-center space-x-1 px-3 py-1.5 bg-input rounded-lg text-sm"
                                >
                                    <FiCalendar size={16} />
                                    <span>{selectedMonth}</span>
                                </button>
                            </div>
                            <button
                                onClick={handleMonthButtonClick} // Open sheet on click
                                className="p-2 bg-input rounded-lg"
                            >
                                <FiSliders size={18} />
                            </button>
                        </div>

                        <div className='flex flex-col flex-grow overflow-y-auto w-full max-w-sm'>

                            {/* Spending List */}
                            <div className="flex-grow p-4 space-y-3">
                                {!isLoading && spendingByTag.length === 0 && (
                                    <p className="text-muted-foreground text-center pt-10">No spending data for this period.</p>
                                )}
                                {!isLoading && spendingByTag.map((item, index) => {
                                    const percentage = totalSpending > 0 ? ((item.amount / totalSpending) * 100).toFixed(1) : '0.0';
                                    const barWidthPercentage = totalSpending > 0 ? (item.amount / totalSpending) * 100 : 0;
                                    return (
                                        <div
                                            key={`${item.key}-${index}`}
                                            className="flex bg-card p-4 rounded-xl overflow-hidden shadow-sm relative border-[1px] border-border cursor-pointer"
                                            onClick={() => handleTagItemClick(item)}
                                        >
                                            <div className="flex w-full justify-between items-end space-x-4" style={{ zIndex: 2 }}>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium truncate text-card-foreground">{item.name}</p>
                                                    <CurrencyDisplay
                                                        amount={item.amount}
                                                        className="text-lg font-bold text-card-foreground"
                                                        showType={false}
                                                        showFraction={false}
                                                    />
                                                </div>
                                                <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                                                    {percentage}%
                                                </span>
                                            </div>
                                            <div
                                                className="absolute inset-y-0 left-0 bg-input/70 rounded-r-xl border-r border-border/50"
                                                style={{ width: `${barWidthPercentage}%`, zIndex: 1 }}
                                                aria-hidden="true"
                                            ></div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Month Filter Bottom Sheet */}
            <DraggableBottomSheet
                isOpen={isMonthFilterOpen}
                onClose={() => setIsMonthFilterOpen(false)}
                zIndex={50}
                title="Filter" // Add title to the sheet
            >
                <div className="flex flex-col h-full px-4 py-4">

                    {/* Filter Content Area - Now uses Flexbox */}
                    <div className="flex-grow overflow-y-auto space-x-4 flex">
                        {/* Month Section */}
                        <div className="flex-1 space-y-4">
                            <h3 className="text-base font-semibold text-muted-foreground mb-2 px-1">Month</h3>
                            <div className="space-y-1">
                                {availableMonths.map((month) => (
                                    <button
                                        key={month.name}
                                        onClick={() => handleMonthSelect(month.name)}
                                        className={`w-full flex justify-between items-center p-3 rounded-lg text-left ${selectedMonth === month.name ? 'bg-muted' : 'hover:bg-muted/50' // Use selectedMonth
                                            }`}
                                    >
                                        <span className="text-sm font-medium text-foreground">{month.name}</span>
                                        {/* Custom Radio Button */}
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedMonth === month.name ? 'border-primary bg-primary' : 'border-muted-foreground' // Use selectedMonth
                                            }`}>
                                            {selectedMonth === month.name && <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Year Section - New */}
                        <div className="flex-1 space-y-4">
                            <h3 className="text-base font-semibold text-muted-foreground mb-2 px-1">Year</h3>
                            <div className="space-y-1">
                                {availableYears.map((year) => (
                                    <button
                                        key={year}
                                        onClick={() => handleYearSelect(year)}
                                        className={`w-full flex justify-between items-center p-3 rounded-lg text-left ${selectedYear === year ? 'bg-muted' : 'hover:bg-muted/50' // Use selectedYear
                                            }`}
                                    >
                                        <span className="text-sm font-medium text-foreground">{year}</span>
                                        {/* Custom Radio Button */}
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedYear === year ? 'border-primary bg-primary' : 'border-muted-foreground' // Use selectedYear
                                            }`}>
                                            {selectedYear === year && <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* End Year Section */}

                        {/* Add other filter sections here (e.g., Sort) */}
                    </div>
                </div>
            </DraggableBottomSheet>

        </ScreenContainer>
    );
};

export default SpendingSummaryScreen; 
